/**
 * @file components/billing/BoostCatalog.jsx
 * @description The boost catalog: one tile per boost with its icon, name, one-line description and credit cost.
 *   One render path for the Billing credits card ("What credits buy") and the public agency page's optional
 *   boosts section. Costs come from boostProducts (boostService.getBoostProducts over platform_config), never
 *   hardcoded; names, icons and colors from components/boostDisplay.
 * @module components/billing/BoostCatalog
 */
import { T, SIZE } from "../../theme";
import { BOOST_DISPLAY, BOOST_MENU } from "../boostDisplay";
import { FS, FW, SP, text } from "./drawerStyles";

function BoostCostTile({ t, product, display, wide, creditRange, save }) {
  const Icon = display.icon;
  const cost = product?.credits ?? "?";
  return (
    <div data-testid="boost-tile" style={{ display: "flex", gap: SP.md, alignItems: "flex-start", padding: `${SP.md}px`, borderRadius: SIZE.radius, background: "#fff", border: `1px solid ${T.border}`, minWidth: 0, ...(wide ? { gridColumn: "1 / -1" } : {}) }}>
      <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: SIZE.radius, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${display.accent}14`, color: display.accent }}>
        <Icon size={16} color={display.accent} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: SP.sm }}>
          <span style={{ ...text.strong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(display.nameKey)}</span>
          <span style={{ ...text.num, fontSize: FS.caption, fontWeight: FW.bold, color: display.accent, whiteSpace: "nowrap" }}>
            {product?.perRecipient ? t("billing.boostCostPerProvider", { count: cost }) : t("billing.boostCost", { count: cost })}
          </span>
        </div>
        {creditRange && typeof cost === "number" && (
          <div data-testid="boost-dollars" style={{ ...text.caption, fontWeight: FW.semibold, color: T.text, marginTop: 2 }}>
            {t(product?.perRecipient ? "billing.boostDollarsPerProvider" : "billing.boostDollars", { min: cost * creditRange.min, max: cost * creditRange.max })}
          </div>
        )}
        <div style={{ ...text.caption, marginTop: 2 }}>{t(save > 0 && display.saveDescKey ? display.saveDescKey : display.descKey, { days: product?.expires ?? 7, save })}</div>
      </div>
    </div>
  );
}

/** Credits the Fast Fill Bundle saves against buying Spotlight and Urgency Badge separately (all from config). */
export function bundleSaving(products) {
  const parts = (products?.spotlight?.credits || 0) + (products?.urgency_badge?.credits || 0);
  const bundle = products?.bundle?.credits;
  return typeof bundle === "number" && parts > bundle ? parts - bundle : 0;
}

/** Two columns on desktop; an odd last tile takes the full row instead of sitting alone.
 *  creditRange ({min, max} dollars per credit, public page only) adds what each boost costs in dollars. */
export default function BoostCatalog({ t, isMobile, boostProducts, creditRange }) {
  return (
    <div data-testid="boost-catalog" style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))", gap: SP.sm }}>
      {BOOST_MENU.map((key, i) => (
        <BoostCostTile key={key} t={t} product={boostProducts?.[key]} display={BOOST_DISPLAY[key]} creditRange={creditRange}
          save={key === "bundle" ? bundleSaving(boostProducts) : 0}
          wide={!isMobile && BOOST_MENU.length % 2 === 1 && i === BOOST_MENU.length - 1} />
      ))}
    </div>
  );
}
